import React from 'react';
import { Alert } from 'antd';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { ADDON_SETTINGS_KEYS } from '@mm/state/interfaces/subscription';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import ShopifyController from '@mm/state/controllers/ShopifyController';
import { useSelector } from 'react-redux';
import {
  selectIsFreeShopify,
  selectIsShopifyContext,
  selectShopifyShop,
} from '@mm/state/selectors/viewSelectors';
import { shopifyRoutes } from '../../../../../../../../modules/Shopify/constants';
import { useHistory } from 'react-router-dom';

const LimitsAlert = ({
  isSubscriptionLimit,
}: {
  isSubscriptionLimit: boolean;
}) => (
  <Alert
    type="warning"
    closable={false}
    message={
      isSubscriptionLimit ? (
        <div>
          If you set a limit for the whole company, your{' '}
          <b>page(s) limits will be reset</b> to “No limit”.
          <div>
            You can set a limit for a company <b>or</b> limits for page(s) only.
          </div>
        </div>
      ) : (
        <ol className="billing-page-card__limit-alert-list">
          If you set a limit for this page:
          <li>
            Company and your other pages <b>limits will be reset</b> to “No
            limit”.
          </li>
          <li>
            {' '}
            <b>You can</b> set your other pages limit.
          </li>
          <li>
            <b>You cannot</b> set a total limit for your company.
          </li>
          <li>
            X-Ray lead <b>capture will be paused</b> when the limit is reached.
          </li>
        </ol>
      )
    }
    className="billing-page-card__limit-alert"
    showIcon
  />
);

export const useXRayBillingLimit = ({
  limitType,
  facebookPage,
  subscription,
  onSuccess,
}: {
  facebookPage?: EnhancedBillingPage;
  subscription?: SubscriptionModel | undefined;
  onSuccess?: () => void;
  limitType: 'subscription' | 'page';
}) => {
  const { openBillingLimitsModal, closeModal } = useModalActions();
  const { update: updateSubscription } = useSubscriptionCRUDActions();
  const { update: updateFacebookPage } = useConnectedPageCRUDActions();
  const isSubscriptionLimit = limitType === 'subscription';

  const history = useHistory();
  const isShopify = useSelector(selectIsShopifyContext);
  const shop = useSelector(selectShopifyShop);
  const isShopifyFree = useSelector(selectIsFreeShopify);

  const onUpdateXRayLimitShopify = async (xrayLimit: number | null) => {
    if (shop?.id && xrayLimit) {
      const res = await ShopifyController.updateCappedAmount(
        shop.id,
        xrayLimit,
      );
      if (res?.updateCappedAmountUrl) {
        window.location.href = res.updateCappedAmountUrl;
      }
    }
  };

  const onUpdateXRayLimit = async (xrayLimit: number | null) => {
    console.debug(isSubscriptionLimit, xrayLimit, facebookPage);
    if (
      !isSubscriptionLimit &&
      facebookPage &&
      xrayLimit !== facebookPage?.settings?.xrayLimit
    ) {
      await updateFacebookPage({
        id: facebookPage.id,
        settings: { xrayLimit },
      });
      onSuccess?.();
    }
    if (
      isSubscriptionLimit &&
      subscription &&
      xrayLimit !== subscription.xrayLimit
    ) {
      await updateSubscription(subscription.id, { xrayLimit });
      onSuccess?.();
    }

    closeModal();
  };

  const title = isSubscriptionLimit
    ? 'Monthly X-Ray Pixel  Capture Company Limit'
    : `${facebookPage?.name} Monthly X-Ray Pixel Capture Limit`;

  const defaultLimitValue =
    !isSubscriptionLimit && facebookPage?.settings?.xrayLimit
      ? facebookPage?.settings?.xrayLimit
      : subscription?.xrayLimit ||
        subscription?.addonUsage?.[ADDON_SETTINGS_KEYS.xrayLeads]?.included;

  const updateXRayLimit = () => {
    if (isShopify && isShopifyFree) {
      history.push(shopifyRoutes.billing.path);
      return;
    }

    openBillingLimitsModal({
      title,
      minValue: isSubscriptionLimit
        ? subscription?.addonUsage?.[ADDON_SETTINGS_KEYS.xrayLeads]?.included
        : 10,
      defaultValue: defaultLimitValue,
      limitComponent: isShopify ? (
        <></>
      ) : (
        <LimitsAlert isSubscriptionLimit={isSubscriptionLimit} />
      ),
      onSubmit: isShopify ? onUpdateXRayLimitShopify : onUpdateXRayLimit,
    });
  };

  return { updateXRayLimit };
};
